import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Fuel, Menu, X, LogOut, Calculator, TrendingUp, User } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store/useAuthStore';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Navigation items common to all users
  const navigation = [
    {
      name: 'Valuation',
      href: '/valuation',
      icon: <Calculator className="w-4 h-4" />,
      disabled: false
    },
    {
      name: 'Insights',
      href: '/blog',
      icon: <TrendingUp className="w-4 h-4" />,
      disabled: false
    },
    {
      name: 'Dashboard',
      href: user ? '/dashboard' : '/dashboard-preview',
      icon: <Fuel className="w-4 h-4" />,
      disabled: false
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link 
            to="/"
            className="flex items-center gap-2 shrink-0"
          >
            <Fuel className="w-7 h-7 text-fc-orange-500" />
            <span className="font-bold text-xl whitespace-nowrap">Fuel Central</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-between w-full ml-6">
            {user ? (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-4">
                  {/* Show all navigation links for logged in users */}
                  {navigation.map((item) => (
                    item.disabled ? (
                      <span
                        key={item.name}
                        className="text-gray-300 cursor-not-allowed flex items-center gap-1 mr-6 text-left"
                      >
                        {item.icon}
                        {item.name}
                      </span>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={(e) => {
                          // If clicking Valuation while already on valuation page, force reload
                          if (item.href === '/valuation' && location.pathname === '/valuation') {
                            e.preventDefault();
                            window.location.href = '/valuation';
                          }
                        }}
                        className={cn(
                          "text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1 mr-6 text-left",
                          location.pathname === item.href && "text-blue-600 font-semibold"
                        )}
                      >
                        {item.icon}
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
                
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">
                    {user.subscription_tier === 'pro' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                        Pro
                      </span>
                    )}
                    {user.subscription_tier === 'enterprise' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">
                        Enterprise
                      </span>
                    )}
                  </span>
                  <Link
                    to="/user-settings"
                    className={cn(
                      "flex items-center gap-2 text-gray-600 hover:text-blue-600",
                      location.pathname === '/user-settings' && "text-blue-600 font-semibold"
                    )}
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-4">
                  {/* Navigation links on the left */}
                  {navigation.map((item) => (
                    item.disabled ? (
                      <span
                        key={item.name}
                        className="text-gray-300 cursor-not-allowed flex items-center gap-1 mr-6 text-left"
                      >
                        {item.icon}
                        {item.name}
                      </span>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={(e) => {
                          // If clicking Valuation while already on valuation page, force reload
                          if (item.href === '/valuation' && location.pathname === '/valuation') {
                            e.preventDefault();
                            window.location.href = '/valuation';
                          }
                        }}
                        className={cn(
                          "text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1 mr-6 text-left",
                          location.pathname === item.href && "text-blue-600 font-semibold"
                        )}
                      >
                        {item.icon}
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
                <div className="flex items-center">
                  <Link
                    to="/login"
                    className="text-gray-700 px-6 py-2 rounded-xl font-semibold hover:text-blue-700 transition-colors text-lg"
                  >
                    Sign In
                  </Link>
                  {/* Direct signup link (Google SSO focused approach) */}
                  <Link
                    to="/signup"
                    className="bg-green-600 text-white px-6 py-2 rounded-xl font-semibold hover:bg-green-700 transition-colors text-lg ml-4 shadow-sm"
                  >
                    Sign Up
                  </Link>
                </div>
                {/* Subscription tier link - temporarily disabled
                <Link
                  to="/subscription-tier"
                  className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg ml-4"
                >
                  Sign Up
                </Link>
                */}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="container mx-auto px-4 py-4 space-y-4">
            {/* Mobile Navigation */}
            {navigation.map((item) => (
              item.disabled ? (
                <span
                  key={item.name}
                  className="block text-gray-300 cursor-not-allowed text-lg flex items-center gap-2"
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "block text-gray-600 hover:text-blue-600 transition-colors text-lg flex items-center gap-2",
                    location.pathname === item.href && "text-blue-600 font-semibold"
                  )}
                  onClick={(e) => {
                    setIsMenuOpen(false);
                    // If clicking Valuation while already on valuation page, force reload
                    if (item.href === '/valuation' && location.pathname === '/valuation') {
                      e.preventDefault();
                      window.location.href = '/valuation';
                    }
                  }}
                >
                  {item.name}
                </Link>
              )
            ))}
            {user ? (
              <>
                <Link
                  to="/user-settings"
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full flex items-center gap-2 text-gray-600 hover:text-blue-600 mt-4 pt-4 border-t"
                >
                  <User className="w-4 h-4" />
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 text-gray-600 hover:text-blue-600 mt-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                <Link
                  to="/login"
                  className="block w-full bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign In
                </Link>
                {/* Direct signup link (Google SSO focused approach) */}
                <Link
                  to="/signup"
                  className="block w-full bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
                {/* Subscription tier link - temporarily disabled
                <Link
                  to="/subscription-tier"
                  className="block w-full bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors text-lg text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
                */}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}